import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AgencyDriverManagement.css';
import { getDrivers, createDriver, updateDriver, deleteDriver } from '../../services/driversService';
import { getDriverDocuments, createDriverDocument } from '../../services/driverDocumentsService';
import { getVehicles } from '../../services/vehiclesService';
import { getAgencyById } from '../../services/agenciesService';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';

const AgencyDriverManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [documentValidityFilter, setDocumentValidityFilter] = useState('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [saveConfirmType, setSaveConfirmType] = useState(null); // 'add' or 'update'
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('add'); // 'add' or 'update'
  const [successDriverId, setSuccessDriverId] = useState('');
  const [successDriverName, setSuccessDriverName] = useState('');
  const [showDocumentSaveConfirm, setShowDocumentSaveConfirm] = useState(false);
  const [showDocumentSuccess, setShowDocumentSuccess] = useState(false);
  const [uploadedDocumentNames, setUploadedDocumentNames] = useState([]);
  const [showDeleteDriverConfirm, setShowDeleteDriverConfirm] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedDriverInfo, setDeletedDriverInfo] = useState(null);
  const [selectedDriverForDocs, setSelectedDriverForDocs] = useState(null);
  const [activeDocTab, setActiveDocTab] = useState('drivingLicense');
  const [documentDetails, setDocumentDetails] = useState({
    drivingLicense: { expiryDate: '', status: 'Valid', lastUpdated: '', documentNumber: '' },
    aadharCard: { expiryDate: '', status: 'Valid', lastUpdated: '', documentNumber: '' },
    panCard: { expiryDate: '', status: 'Valid', lastUpdated: '', documentNumber: '' },
    medicalCertificate: { expiryDate: '', status: 'Valid', lastUpdated: '', documentNumber: '' }
  });
  const [documentPreviews, setDocumentPreviews] = useState({
    drivingLicense: null,
    aadharCard: null,
    panCard: null,
    medicalCertificate: null
  });
  const [documentFiles, setDocumentFiles] = useState({
    drivingLicense: null,
    aadharCard: null,
    panCard: null,
    medicalCertificate: null
  });
  const [documentFileNames, setDocumentFileNames] = useState({
    drivingLicense: null,
    aadharCard: null,
    panCard: null,
    medicalCertificate: null
  });
  const [originalDocumentFileNames, setOriginalDocumentFileNames] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSampleCSVModal, setShowSampleCSVModal] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importValidDrivers, setImportValidDrivers] = useState([]);
  const [importInvalidRows, setImportInvalidRows] = useState([]);
  const [showImportSuccess, setShowImportSuccess] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(0);
  const [importSkippedCount, setImportSkippedCount] = useState(0);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [currentAgencyId, setCurrentAgencyId] = useState(null);
  const [currentAgencyName, setCurrentAgencyName] = useState('');
  
  // Form state - declared early so it can be used in useEffect hooks
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    dateOfBirth: '',
    address: '',
    alternateNumber: '',
    driverStatus: 'Active',
    joiningDate: '',
    licenseNumber: '',
    aadharNumber: '',
    licenseType: '',
    panNumber: '',
    medicalCertificate: '',
    agencyId: null,
    agencyName: '',
    assignedVehicleId: null,
    photo: null,
    photoPreview: null
  });

  const statusDropdownRef = useRef(null);
  const documentDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);

  const defaultDocumentsExpiry = { total: 4, valid: 4, expired: 0 };

  // Map API response to component format
  const mapDriverFromAPI = (apiDriver, agencyLookup = null) => {
    // Extract agency name from nested agency object or fallback to agency_name/agency_id
    let agencyName = apiDriver.agency?.agency_name || 
                     apiDriver.agency?.name || 
                     apiDriver.agency?.company_name ||
                     apiDriver.agency_name || 
                     '';
    
    // If agency name is still not found and we have agency_id, try lookup map
    if (!agencyName && apiDriver.agency_id && agencyLookup) {
      agencyName = agencyLookup.get(apiDriver.agency_id) || '';
    }
    
    // Final fallback
    if (!agencyName) {
      agencyName = apiDriver.agency_id ? `Agency #${apiDriver.agency_id}` : 'Not Assigned';
    }
    
    return {
      // `id` should be the primary DB id (used in update/delete URLs).
      id: apiDriver.id ?? apiDriver.driver_id,
      // driverCode is the business driver_id string/code from API
      driverCode: apiDriver.driver_id ?? apiDriver.id,
      fullName: apiDriver.full_name || 'Unknown Driver',
      mobileNumber: apiDriver.mobile_number || '',
      licenseNumber: apiDriver.license_number || '',
      licenseType: apiDriver.license_type || '',
      agency: agencyName,
      agencyId: apiDriver.agency_id,
      assignedVehicleId: apiDriver.assigned_vehicle_id || apiDriver.assigned_vehicle?.id || apiDriver.vehicle_id || null,
      status: apiDriver.driver_status || 'Active',
      assignedVehicle: (apiDriver.assigned_vehicle && (apiDriver.assigned_vehicle.vehicle_number || apiDriver.assigned_vehicle.vehicle_id)) || apiDriver.vehicle_number || 'Not Assigned',
      documentsExpiry: apiDriver.documents_expiry || defaultDocumentsExpiry,
      documents: apiDriver.documents || {},
      photo: apiDriver.photo_url || null,
      photoUrl: apiDriver.photo_url || null,
      joiningDate: apiDriver.joining_date || '',
      dateOfBirth: apiDriver.date_of_birth || '',
      address: apiDriver.address || '',
      alternateNumber: apiDriver.alternate_number || '',
      aadharNumber: apiDriver.aadhar_number || '',
      panNumber: apiDriver.pan_number || '',
      // support both possible API keys
      medicalCertificate: apiDriver.medical_certificate_number || apiDriver.medical_certificate || '',
    };
  };

  // Get current agency ID and name from localStorage, fetch from API if name is missing
  useEffect(() => {
    const loadAgencyInfo = async () => {
      // Try agencyProfile first
      let agency = null;
      const savedAgency = localStorage.getItem('agencyProfile');
      if (savedAgency) {
        try {
          agency = JSON.parse(savedAgency);
        } catch (error) {
          console.error('Error parsing agencyProfile:', error);
        }
      }
      
      // If not found, try userData (might contain agency info)
      if (!agency) {
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            // Check if user has agency info
            if (user.agency_id || user.agency) {
              agency = {
                id: user.agency_id || user.agency?.id,
                agency_id: user.agency_id || user.agency?.id,
                name: user.agency?.name || user.agency?.agency_name || user.agency_name || '',
                agency_name: user.agency?.agency_name || user.agency_name || '',
                company_name: user.agency?.company_name || ''
              };
            }
          } catch (error) {
            console.error('Error parsing userData:', error);
          }
        }
      }
      
      if (agency) {
        console.log('Agency Driver Management: Raw agency data:', agency);
        const agencyId = agency.id || agency.agency_id || null;
        // Try multiple possible field names for agency name
        let agencyName = agency.name || agency.agency_name || agency.company_name || agency.agencyName || '';
        console.log('Agency Driver Management: Extracted agency ID:', agencyId, 'Agency Name:', agencyName);
        
        if (agencyId) {
          setCurrentAgencyId(agencyId);
          
          // If agency name is empty, fetch it from API
          if (!agencyName && agencyId) {
            try {
              console.log('Agency Driver Management: Agency name is empty, fetching from API for ID:', agencyId);
              const agencyResponse = await getAgencyById(agencyId);
              const agencyData = agencyResponse.data || agencyResponse;
              agencyName = agencyData.agency_name || agencyData.name || agencyData.company_name || '';
              console.log('Agency Driver Management: Fetched agency name from API:', agencyName);
              
              // Update localStorage with the fetched name
              if (agencyName) {
                const updatedAgency = {
                  ...agency,
                  name: agencyName,
                  agency_name: agencyName,
                  company_name: agencyName
                };
                localStorage.setItem('agencyProfile', JSON.stringify(updatedAgency));
              }
            } catch (error) {
              console.error('Agency Driver Management: Error fetching agency from API:', error);
              // Continue with empty name if API call fails
            }
          }
          
          setCurrentAgencyName(agencyName);
          // Initialize form with agency info
          setFormData(prev => ({
            ...prev,
            agencyId: agencyId,
            agencyName: agencyName
          }));
          console.log('Agency Driver Management: Set formData.agencyName to:', agencyName);
        } else {
          console.warn('Agency Driver Management: No agency ID found in agency data');
          setCurrentAgencyId(null);
          setCurrentAgencyName('');
        }
      } else {
        console.warn('Agency Driver Management: No agency data found in localStorage (checked agencyProfile and userData)');
        setCurrentAgencyId(null);
        setCurrentAgencyName('');
      }
    };
    
    loadAgencyInfo();
  }, []);

  // Fetch drivers from API
  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      // Filter by current agency ID for agency users
      if (currentAgencyId) {
        params.agency_id = currentAgencyId;
        console.log('Agency Driver Management: Fetching drivers with params:', params);
      }

      console.log('Agency Driver Management: Calling getDrivers with params:', params);
      let response;
      let usedFallback = false;
      try {
        response = await getDrivers(params);
      } catch (apiError) {
        // If API fails with agency_id filter, try fetching all drivers and filter client-side
        const is500Error = apiError.response?.status === 500 || 
                          (apiError.message && apiError.message.includes('500')) ||
                          (apiError.message && apiError.message.includes('Internal Server Error'));
        
        if (currentAgencyId && is500Error) {
          console.warn('Agency Driver Management: API returned 500 error with agency_id filter.');
          console.warn('Trying fallback: Fetching all drivers without agency_id filter, then filtering client-side...');
          
          const fallbackParams = {
            page: currentPage,
            limit: itemsPerPage * 10, // Fetch more to ensure we get all drivers for the agency
          };
          if (searchQuery) fallbackParams.search = searchQuery;
          if (statusFilter) fallbackParams.status = statusFilter;
          // Don't include agency_id in fallback call
          
          try {
            response = await getDrivers(fallbackParams);
            usedFallback = true;
            console.log('Agency Driver Management: Fallback API call succeeded, will filter client-side');
          } catch (fallbackError) {
            console.error('Agency Driver Management: Fallback API call also failed:', fallbackError);
            // If fallback also fails, throw the original error
            throw apiError;
          }
        } else {
          throw apiError;
        }
      }
      console.log('Agency Driver Management: API response:', response);
      console.log('Agency Driver Management: Response type:', typeof response, 'Is array:', Array.isArray(response));

      // Handle different API response structures
      let items = [];
      let pagination = {};
      
      // Case 1: Response is an array directly
      if (Array.isArray(response)) {
        items = response;
        pagination = { total: response.length };
      }
      // Case 2: Response has data property
      else if (response.data) {
        // Case 2a: response.data is an array
        if (Array.isArray(response.data)) {
          items = response.data;
          pagination = response.pagination || { total: response.data.length };
        }
        // Case 2b: response.data has items property
        else if (response.data.items) {
          items = response.data.items || [];
          pagination = response.data.pagination || response.pagination || {};
        }
        // Case 2c: response.data.data has items
        else if (response.data.data && response.data.data.items) {
          items = response.data.data.items || [];
          pagination = response.data.data.pagination || response.data.pagination || response.pagination || {};
        }
        // Case 2d: response.data is an object with data property
        else {
          items = response.data.items || [];
          pagination = response.data.pagination || response.pagination || {};
        }
      }
      // Case 3: Response has items property directly
      else if (response.items) {
        items = response.items || [];
        pagination = response.pagination || {};
      }
      // Case 4: Default fallback
      else {
        items = [];
        pagination = {};
      }

      console.log('Agency Driver Management: Extracted items:', items.length, 'items:', items);
      console.log('Agency Driver Management: Pagination:', pagination);

      // Create agency lookup map for resolving agency names
      const agencyLookup = new Map();
      try {
        // Fetch agencies to create lookup map
        const { getAgencies } = await import('../../services/agenciesService');
        const res = await getAgencies({ page: 1, limit: 500 });
        const responseData = res.data || res;
        const agencyItems = responseData.data?.items || responseData.items || responseData.data || [];
        const agenciesList = agencyItems.map(a => ({ 
          id: a.id || a.agency_id, 
          name: a.name || a.agency_name || a.company_name || (a.id ? `Agency #${a.id}` : 'Unknown') 
        }));
        agenciesList.forEach(agency => {
          if (agency.id && agency.name) {
            agencyLookup.set(agency.id, agency.name);
          }
        });
      } catch (err) {
        console.warn('Failed to fetch agencies for lookup', err);
      }

      let mappedDrivers = items.map(driver => mapDriverFromAPI(driver, agencyLookup));
      console.log('Agency Driver Management: Mapped drivers:', mappedDrivers.length, 'drivers:', mappedDrivers);
      
      // Consolidate duplicate drivers (same driver assigned to multiple vehicles)
      const driverMap = new Map();
      mappedDrivers.forEach(driver => {
        const driverId = String(driver.id);
        if (driverMap.has(driverId)) {
          // Driver already exists, add vehicle to the list
          const existingDriver = driverMap.get(driverId);
          const currentVehicle = driver.assignedVehicle && driver.assignedVehicle !== 'Not Assigned' 
            ? driver.assignedVehicle 
            : null;
          
          if (currentVehicle) {
            // Get existing vehicles
            const existingVehicles = existingDriver.assignedVehicle && existingDriver.assignedVehicle !== 'Not Assigned'
              ? existingDriver.assignedVehicle.split(', ').filter(v => v.trim())
              : [];
            
            // Add new vehicle if not already in list
            if (!existingVehicles.includes(currentVehicle)) {
              existingVehicles.push(currentVehicle);
            }
            
            // Combine vehicles with comma separation
            existingDriver.assignedVehicle = existingVehicles.length > 0 
              ? existingVehicles.join(', ') 
              : 'Not Assigned';
          }
        } else {
          // First occurrence of this driver
          driverMap.set(driverId, { ...driver });
        }
      });
      
      // Convert map back to array
      mappedDrivers = Array.from(driverMap.values());
      
      // Apply client-side agency filter (always filter for agency users, especially if we used fallback)
      if (currentAgencyId && mappedDrivers.length > 0) {
        const beforeFilter = mappedDrivers.length;
        mappedDrivers = mappedDrivers.filter(d => {
          const driverAgencyId = d.agencyId || d.agency_id;
          // Only show drivers that match the current agency ID
          const matches = driverAgencyId && String(driverAgencyId) === String(currentAgencyId);
          if (!matches && driverAgencyId) {
            console.log('Agency Driver Management: Filtered out driver:', d.id, 'agencyId:', driverAgencyId, 'expected:', currentAgencyId);
          }
          return matches;
        });
        console.log('Agency Driver Management: After agency filter:', mappedDrivers.length, 'drivers (was', beforeFilter, 'before filter)');
        if (usedFallback) {
          console.log('Agency Driver Management: Note: Used fallback method due to backend 500 error with agency_id filter');
        }
      } else if (currentAgencyId && mappedDrivers.length === 0) {
        console.log('Agency Driver Management: No drivers returned from API. This could mean:');
        console.log('  1. No drivers exist for agency_id:', currentAgencyId);
        console.log('  2. Backend filtering is working but no matches found');
        console.log('  3. API response structure is different than expected');
      }
      
      // apply client-side status filter if backend doesn't respect the status query param
      const visibleDrivers = statusFilter
        ? mappedDrivers.filter(d => String(d.status).toLowerCase() === String(statusFilter).toLowerCase())
        : mappedDrivers;

      setDrivers(visibleDrivers);
      setTotalCount(pagination.total || visibleDrivers.length);
      
      // Enrich drivers with real-time document data for the current list
      (async () => {
        try {
          const enrichPromises = visibleDrivers.map(async (drv) => {
            try {
              const docs = await getDriverDocuments(drv.id);
              const docsArray = Array.isArray(docs) ? docs : Object.values(docs || {});
              const docsMap = {};
              docsArray.forEach((d) => {
                const key = Object.keys(documentDetails).find(k => getDocumentLabel(k).toLowerCase() === (d.document_type || '').toLowerCase()) || null;
                if (!key) return;
                docsMap[key] = d;
              });
              const total = Object.keys(documentDetails).length;
              let valid = 0;
              let expired = 0;
              Object.keys(documentDetails).forEach(k => {
                const d = docsMap[k] || {};
                const hasFile = Boolean(d.file_name || d.fileName || d.document_url || d.url || d.preview);
                const dateStr = d.expiry_date || d.date || d.validTill || '';
                let isExpired = false;
                if (dateStr) {
                  const dt = new Date(dateStr);
                  if (!isNaN(dt.getTime()) && dt < new Date()) {
                    isExpired = true;
                    expired++;
                  }
                }
                // A document is valid only if it has a file AND is not expired
                if (hasFile && !isExpired) {
                  valid++;
                }
              });
              return { id: drv.id, documents: docsMap, documentsExpiry: { total, valid, expired } };
            } catch (err) {
              console.warn('Failed to fetch docs for driver', drv.id, err);
              return { id: drv.id, documents: drv.documents || {}, documentsExpiry: drv.documentsExpiry || { total: Object.keys(documentDetails).length, valid: 0, expired: 0 } };
            }
          });
          const enriched = await Promise.all(enrichPromises);
          setDrivers(prev => prev.map(drv => {
            const e = enriched.find(x => String(x.id) === String(drv.id));
            if (!e) return drv;
            return { ...drv, documents: e.documents, documentsExpiry: e.documentsExpiry };
          }));
        } catch (err) {
          console.warn('Failed to enrich drivers with documents', err);
        }
      })();
    } catch (err) {
      console.error('Agency Driver Management: Error fetching drivers:', err);
      console.error('Agency Driver Management: Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      setDrivers([]);
      setTotalCount(0);
      setError(err.message || 'Failed to fetch drivers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, currentAgencyId]);

  // Fetch drivers on mount and when pagination/filters change
  useEffect(() => {
    // Only fetch if we have a valid agency ID (not null, not undefined, and not empty string)
    if (currentAgencyId !== null && currentAgencyId !== undefined && currentAgencyId !== '') {
      console.log('Agency Driver Management: Fetching drivers for agency ID:', currentAgencyId);
      fetchDrivers();
    } else {
      // If agency ID is not available yet, wait (don't show empty state immediately)
      // This prevents race condition where fetchDrivers is called before agency ID is loaded
      if (currentAgencyId === null) {
        console.log('Agency Driver Management: Waiting for agency ID to be loaded...');
      }
    }
  }, [fetchDrivers, currentAgencyId]);

  // Debounced search - reset to page 1 when search query changes
  useEffect(() => {
    if (searchQuery && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery]);

  // Fetch vehicles for dropdown (filtered by agency)
  const fetchVehicles = useCallback(async () => {
    try {
      const params = { page: 1, limit: 500 }; // Fetch all vehicles for dropdown
      if (currentAgencyId) {
        params.agency_id = currentAgencyId;
      }
      
      const response = await getVehicles(params);
      
      // Handle different response structures
      let vehiclesData = [];
      if (response.data && Array.isArray(response.data)) {
        vehiclesData = response.data;
      } else if (Array.isArray(response)) {
        vehiclesData = response;
      } else {
        vehiclesData = response.data?.items || response.items || [];
      }
      
      // Ensure vehiclesData is an array
      if (!Array.isArray(vehiclesData)) {
        vehiclesData = [];
      }
      
      setVehicles(vehiclesData);
      setFilteredVehicles(vehiclesData);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setVehicles([]);
      setFilteredVehicles([]);
    }
  }, [currentAgencyId]);

  // Fetch vehicles on component mount
  useEffect(() => {
    if (currentAgencyId !== null) {
      fetchVehicles();
    }
  }, [fetchVehicles, currentAgencyId]);

  // Filter by document validity (client-side only, since API handles search and status)
  const filteredDrivers = drivers.filter(driver => {
    if (!documentValidityFilter) return true;
    
    const { total, valid, expired } = driver.documentsExpiry || defaultDocumentsExpiry;
    if (documentValidityFilter === 'Valid') return expired === 0 && valid === total;
    if (documentValidityFilter === 'Expired') return expired > 0;
    if (documentValidityFilter === 'Expire Soon') return expired === 0 && valid < total && valid > 0;
    return true;
  });

  // Use API pagination - drivers already contains the current page's data
  // Apply document validity filter client-side if needed
  const currentDrivers = filteredDrivers;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + currentDrivers.length, totalCount);

  // Reset to page 1 when itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Auto-hide success modals
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    if (showDeleteSuccess) {
      const timer = setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showDeleteSuccess]);

  useEffect(() => {
    if (showDocumentSuccess) {
      const timer = setTimeout(() => {
        setShowDocumentSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showDocumentSuccess]);

  useEffect(() => {
    if (showImportSuccess) {
      const timer = setTimeout(() => {
        setShowImportSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showImportSuccess]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (documentDropdownRef.current && !documentDropdownRef.current.contains(event.target)) {
        setDocumentDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper functions
  const getStatusBadgeClass = (status) => {
    return status === 'Active' ? 'status-badge active' : 'status-badge inactive';
  };

  const getDocumentProgressClass = (docExpiry) => {
    const { total, valid, expired } = docExpiry;
    if (expired > 0) return 'doc-progress expired';
    if (valid === total) return 'doc-progress valid';
    return 'doc-progress neutral';
  };

  const getProgressPercentage = (docExpiry) => {
    const { total, valid } = docExpiry;
    return total > 0 ? (valid / total) * 100 : 0;
  };

  const handleAddDriver = () => {
    // Use the current agency name from state, or fallback to localStorage
    let agencyName = currentAgencyName;
    if (!agencyName) {
      const savedAgency = localStorage.getItem('agencyProfile');
      if (savedAgency) {
        try {
          const agency = JSON.parse(savedAgency);
          agencyName = agency.name || agency.agency_name || agency.company_name || agency.agencyName || '';
          if (agencyName) {
            setCurrentAgencyName(agencyName);
          }
        } catch (error) {
          console.error('Error loading agency name:', error);
        }
      }
    }

    console.log('handleAddDriver: Setting agencyName to:', agencyName || '(empty)', 'currentAgencyId:', currentAgencyId);

    setFormData({
      fullName: '',
      mobileNumber: '',
      dateOfBirth: '',
      address: '',
      alternateNumber: '',
      driverStatus: 'Active',
      joiningDate: '',
      licenseNumber: '',
      aadharNumber: '',
      licenseType: '',
      panNumber: '',
      medicalCertificate: '',
      agencyId: currentAgencyId,
      agencyName: agencyName || '',
      assignedVehicleId: null,
      photo: null,
      photoPreview: null
    });
    setFormErrors({});
    setFilteredVehicles(vehicles);
    setShowAddModal(true);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({...formData, photo: file, photoPreview: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCloseModal = () => {
    // Use the current agency name from state
    const agencyName = currentAgencyName || '';

    setShowAddModal(false);
    setShowEditModal(false);
    setEditingDriver(null);
    setFormData({
      fullName: '',
      mobileNumber: '',
      dateOfBirth: '',
      address: '',
      alternateNumber: '',
      driverStatus: 'Active',
      joiningDate: '',
      licenseNumber: '',
      aadharNumber: '',
      licenseType: '',
      panNumber: '',
      medicalCertificate: '',
      agencyId: currentAgencyId,
      agencyName: agencyName,
      assignedVehicleId: null,
      photo: null,
      photoPreview: null
    });
    setFormErrors({});
  };

  // Parse CSV text into array of objects using headers (handles quotes and commas)
  const parseCSV = (text) => {
    const rows = [];
    let current = '';
    let inQuotes = false;
    let row = [];
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const next = text[i + 1];
      if (char === '"') {
        if (inQuotes && next === '"') { // Escaped quote
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push(current);
        current = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (current !== '' || row.length > 0) {
          row.push(current);
          rows.push(row);
          row = [];
          current = '';
        }
      } else {
        current += char;
      }
    }
    if (current !== '' || row.length > 0) {
      row.push(current);
      rows.push(row);
    }
    // Remove possible empty last line
    if (rows.length && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0].trim() === '') {
      rows.pop();
    }
    if (rows.length === 0) return [];
    const headers = rows[0].map(h => h.trim());
    return rows.slice(1).map(r => {
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = (r[idx] ?? '').trim();
      });
      return obj;
    });
  };

  // Map CSV row to our driver object
  const mapRowToDriver = (row) => {
    const get = (key) => row[key] ?? '';
    const id = get('Driver ID') || ('DRV' + String(Math.floor(Math.random() * 10000)).padStart(4, '0'));
    const documents = {
      drivingLicense: { 
        fileName: null, 
        preview: null, 
        date: get('Driving License Expiry Date') || '',
        documentNumber: get('License Number') || ''
      },
      aadharCard: { 
        fileName: null, 
        preview: null, 
        date: '', // Aadhar Card does not have expiry date
        documentNumber: get('Aadhar Number') || ''
      },
      panCard: { 
        fileName: null, 
        preview: null, 
        date: '', // PAN Card does not have expiry date
        documentNumber: get('PAN Number') || ''
      },
      medicalCertificate: { 
        fileName: null, 
        preview: null, 
        date: get('Medical Certificate Expiry Date') || '',
        documentNumber: get('Medical Certificate') || ''
      }
    };
    return {
      id,
      fullName: get('Full Name'),
      mobileNumber: get('Mobile Number'),
      alternateNumber: get('Alternate Number') || '',
      dateOfBirth: get('Date of Birth') || '',
      address: get('Address') || '',
      licenseNumber: get('License Number') || '',
      licenseType: get('License Type') || '',
      aadharNumber: get('Aadhar Number') || '',
      panNumber: get('PAN Number') || '',
      joiningDate: get('Joining Date') || '',
      agency: get('Transporter') || get('Agency') || '', // Support both "Transporter" and "Agency"
      status: get('Driver Status') || 'Active',
      assignedVehicle: get('Assigned Vehicle') || 'Not Assigned',
      medicalCertificate: get('Medical Certificate') || '',
      documentsExpiry: { total: 4, valid: 4, expired: 0 },
      documents
    };
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      let rows = [];

      // Check if file is Excel (.xlsx) or CSV
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Read Excel file
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        
        // Convert to same format as CSV parser
        rows = jsonData.map(row => {
          const obj = {};
          Object.keys(row).forEach(key => {
            // Remove asterisk from header names if present
            const cleanKey = key.replace(/\s*\*\s*$/, '').trim();
            obj[cleanKey] = String(row[key] || '').trim();
          });
          return obj;
        });
      } else {
        // Read CSV file
        const text = await file.text();
        rows = parseCSV(text);
      }

      // Filter out empty rows (missing mandatory fields like Full Name)
      const nonEmptyRows = rows.filter(r => (r['Full Name'] || '').trim() !== '');

      // Validation using mobile number (or driver ID if available)
      const existingMobileNumbers = new Set(drivers.map(d => (d.mobileNumber || '').toLowerCase()));
      const existingIds = new Set(drivers.map(d => (d.id || '').toString().toLowerCase()));
      const seenInFile = new Set();
      const valid = [];
      const invalid = [];

      nonEmptyRows.forEach((r, idx) => {
        const fullName = (r['Full Name'] || '').trim();
        const mobileNumber = (r['Mobile Number'] || '').trim();
        const driverId = (r['Driver ID'] || '').trim().toLowerCase();
        const mobileLower = mobileNumber.toLowerCase();
        
        if (!fullName) {
          invalid.push({ row: idx + 2, driverName: fullName, reason: 'Full Name missing' });
        } else if (driverId && existingIds.has(driverId)) {
          invalid.push({ row: idx + 2, driverName: fullName, reason: 'Duplicate Driver ID' });
        } else if (mobileNumber && existingMobileNumbers.has(mobileLower)) {
          invalid.push({ row: idx + 2, driverName: fullName, reason: 'Duplicate Mobile Number' });
        } else if (mobileNumber && seenInFile.has(mobileLower)) {
          invalid.push({ row: idx + 2, driverName: fullName, reason: 'Duplicate Mobile Number in file' });
        } else {
          if (mobileNumber) seenInFile.add(mobileLower);
          valid.push(mapRowToDriver(r));
        }
      });

      setImportFileName(file.name);
      setImportValidDrivers(valid);
      setImportInvalidRows(invalid);
      setShowImportModal(true);
    } catch (err) {
      console.error('Import error:', err);
      alert('Failed to import file. Please ensure it matches the sample format. Error: ' + (err.message || 'Unknown error'));
    } finally {
      e.target.value = '';
    }
  };

  const handleImportConfirm = async () => {
    if (importValidDrivers.length === 0) return;

    try {
      setLoading(true);
      setError('');
      
      let successCount = 0;
      let failedCount = 0;
      const failedDrivers = [];

      // Process each driver
      for (const driver of importValidDrivers) {
        try {
          // For agency users, use current agency ID
          const agencyId = currentAgencyId || driver.agencyId;
          
          // Ensure agencyId is an integer or null
          let finalAgencyId = agencyId;
          if (finalAgencyId !== null && finalAgencyId !== undefined) {
            finalAgencyId = parseInt(finalAgencyId, 10);
            if (isNaN(finalAgencyId)) {
              finalAgencyId = null;
            }
          } else {
            finalAgencyId = null;
          }

          // Find vehicle ID from vehicle number (optional)
          let assignedVehicleId = driver.assignedVehicleId;
          if (!assignedVehicleId && driver.assignedVehicle && driver.assignedVehicle !== 'Not Assigned') {
            const vehicleNumber = driver.assignedVehicle.trim();
            const matchedVehicle = vehicles.find(
              v => {
                const vNum = (v.vehicle_number || v.vehicleNumber || '').trim();
                return vNum.toLowerCase() === vehicleNumber.toLowerCase();
              }
            );
            if (matchedVehicle) {
              assignedVehicleId = matchedVehicle.id || matchedVehicle.vehicle_id;
            }
          }

          // Prepare driver data for API
          const driverData = mapDriverToAPI({
            fullName: driver.fullName,
            mobileNumber: driver.mobileNumber,
            alternateNumber: driver.alternateNumber || null,
            dateOfBirth: driver.dateOfBirth || null,
            address: driver.address || null,
            licenseNumber: driver.licenseNumber || null,
            licenseType: driver.licenseType || null,
            aadharNumber: driver.aadharNumber || null,
            panNumber: driver.panNumber || null,
            joiningDate: driver.joiningDate || null,
            agencyId: finalAgencyId,
            assignedVehicleId: assignedVehicleId || null,
            driverStatus: driver.status || 'Active',
            medicalCertificate: driver.medicalCertificate || null,
            photo: driver.photo || null
          });

          // Create driver via API
          const createResponse = await createDriver(driverData);
          const createdDriver = createResponse?.data || createResponse;
          const driverId = createdDriver?.id || createdDriver?.driver_id;

          // Save documents to driver_documents table if driver was created and documents exist
          if (driverId && driver.documents) {
            try {
              const documentsToSave = [];
              
              // Convert documents from import format to API format
              Object.entries(driver.documents).forEach(([docKey, docData]) => {
                if (docData && (docData.documentNumber || docData.date)) {
                  const documentType = getDocumentLabel(docKey);
                  const documentNumber = docData.documentNumber?.trim();
                  const expiryDate = docData.date?.trim();
                  
                  // Only add if at least document number or expiry date is provided
                  if (documentNumber || (expiryDate && (docKey === 'drivingLicense' || docKey === 'medicalCertificate'))) {
                    // Convert date format if needed
                    let formattedDate = expiryDate;
                    if (expiryDate) {
                      const dateObj = new Date(expiryDate);
                      if (!isNaN(dateObj.getTime())) {
                        formattedDate = formatDateForInput(dateObj.toISOString());
                      } else {
                        // Try common date formats
                        const formats = [
                          /(\d{2})\/(\d{2})\/(\d{4})/, // DD/MM/YYYY
                          /(\d{4})-(\d{2})-(\d{2})/,   // YYYY-MM-DD
                          /(\d{2})-(\d{2})-(\d{4})/    // DD-MM-YYYY
                        ];
                        for (const format of formats) {
                          const match = expiryDate.match(format);
                          if (match) {
                            if (format === formats[0]) {
                              formattedDate = `${match[3]}-${match[2]}-${match[1]}`;
                            } else if (format === formats[1]) {
                              formattedDate = expiryDate;
                            } else {
                              formattedDate = `${match[3]}-${match[2]}-${match[1]}`;
                            }
                            break;
                          }
                        }
                      }
                    }
                    
                    documentsToSave.push({
                      document_type: documentType,
                      document_number: documentNumber || null,
                      expiry_date: formattedDate || null,
                      driver_id: driverId
                    });
                  }
                }
              });

              // Save documents if any were provided
              if (documentsToSave.length > 0) {
                const documentPromises = documentsToSave.map(async (doc) => {
                  const formData = new FormData();
                  formData.append('driver_id', driverId);
                  formData.append('document_type', doc.document_type);
                  if (doc.document_number) {
                    formData.append('document_number', doc.document_number);
                  }
                  if (doc.expiry_date) {
                    formData.append('expiry_date', doc.expiry_date);
                  }
                  return createDriverDocument(formData);
                });
                
                await Promise.all(documentPromises);
                console.log(`Saved ${documentsToSave.length} document(s) for driver ${driver.fullName}`);
              }
            } catch (docError) {
              console.warn(`Failed to save documents for driver ${driver.fullName}:`, docError);
            }
          }

          successCount++;
        } catch (err) {
          console.error(`Failed to create driver ${driver.fullName}:`, err);
          failedCount++;
          failedDrivers.push({
            driverName: driver.fullName,
            error: err.message || 'Unknown error'
          });
        }
      }

      // Update state
      setImportSuccessCount(successCount);
      setImportSkippedCount(importInvalidRows.length + failedCount);
      setShowImportModal(false);
      setShowImportSuccess(true);
      setImportValidDrivers([]);
      setImportInvalidRows([]);
      setImportFileName('');

      // Refresh driver list
      await fetchDrivers();

      // Show error message if some drivers failed
      if (failedCount > 0) {
        const errorMsg = `${failedCount} driver(s) failed to import:\n${failedDrivers.map(d => `- ${d.driverName}: ${d.error}`).join('\n')}`;
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Error during import:', err);
      setError(err.message || 'Failed to import drivers. Please try again.');
      alert(`Import failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCancel = () => {
    setShowImportModal(false);
    setImportValidDrivers([]);
    setImportInvalidRows([]);
    setImportFileName('');
  };

  // Download sample Excel template with formatting
  const handleDownloadSampleCSV = async () => {
    // Define required fields (will have asterisk)
    const requiredFields = ['Full Name', 'Mobile Number', 'Date of Birth', 'Address', 'Driver Status', 'Joining Date'];
    
    const headers = [
      'Full Name',
      'Mobile Number',
      'Date of Birth',
      'Address',
      'Alternate Number',
      'Driver Status',
      'Joining Date',
      'License Number',
      'License Type',
      'Aadhar Number',
      'PAN Number',
      'Medical Certificate',
      'Driving License Expiry Date',
      'Medical Certificate Expiry Date',
      'Transporter',
      'Assigned Vehicle'
    ];

    // Add asterisk to required fields
    const headersWithAsterisks = headers.map(header => 
      requiredFields.includes(header) ? `${header} *` : header
    );

    // Create workbook and worksheet using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Driver Import Template');

    // Add header row
    worksheet.addRow(headersWithAsterisks);

    // Add sample data row with example dates
    const sampleDate = new Date();
    sampleDate.setFullYear(sampleDate.getFullYear() + 1);
    
    const day = String(sampleDate.getDate()).padStart(2, '0');
    const month = String(sampleDate.getMonth() + 1).padStart(2, '0');
    const year = sampleDate.getFullYear();
    const sampleDateFormatted = `${day}/${month}/${year}`;
    
    const dobDate = new Date();
    dobDate.setFullYear(dobDate.getFullYear() - 30);
    const dobDay = String(dobDate.getDate()).padStart(2, '0');
    const dobMonth = String(dobDate.getMonth() + 1).padStart(2, '0');
    const dobYear = dobDate.getFullYear();
    const dobFormatted = `${dobDay}/${dobMonth}/${dobYear}`;
    
    const joiningDate = new Date();
    joiningDate.setMonth(joiningDate.getMonth() - 6);
    const joinDay = String(joiningDate.getDate()).padStart(2, '0');
    const joinMonth = String(joiningDate.getMonth() + 1).padStart(2, '0');
    const joinYear = joiningDate.getFullYear();
    const joiningDateFormatted = `${joinDay}/${joinMonth}/${joinYear}`;
    
    const sampleRow = [
      'John Doe',
      '9876543210',
      dobFormatted,
      '123 Main Street, City, State',
      '9876543211',
      'Active',
      joiningDateFormatted,
      'DL1234567890',
      'LMV',
      '1234 5678 9012',
      'ABCDE1234F',
      'MED123456',
      sampleDateFormatted,
      sampleDateFormatted,
      currentAgencyName || 'Agency Name', // For agency users, this is their own agency
      vehicles.length > 0 ? (vehicles[0].vehicle_number || vehicles[0].vehicleNumber || '') : ''
    ];
    worksheet.addRow(sampleRow);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    };

    // Style sample row
    const sampleDataRow = worksheet.getRow(2);
    sampleDataRow.font = { italic: true, color: { argb: 'FF666666' } };
    sampleDataRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' }
    };

    // Set column widths
    worksheet.getColumn(1).width = 20; // Full Name *
    worksheet.getColumn(2).width = 18; // Mobile Number *
    worksheet.getColumn(3).width = 18; // Date of Birth *
    worksheet.getColumn(4).width = 35; // Address *
    worksheet.getColumn(5).width = 18; // Alternate Number
    worksheet.getColumn(6).width = 18; // Driver Status *
    worksheet.getColumn(7).width = 18; // Joining Date *
    worksheet.getColumn(8).width = 20; // License Number
    worksheet.getColumn(9).width = 15; // License Type
    worksheet.getColumn(10).width = 18; // Aadhar Number
    worksheet.getColumn(11).width = 15; // PAN Number
    worksheet.getColumn(12).width = 22; // Medical Certificate
    worksheet.getColumn(13).width = 28; // Driving License Expiry Date
    worksheet.getColumn(14).width = 30; // Medical Certificate Expiry Date
    worksheet.getColumn(15).width = 25; // Transporter
    worksheet.getColumn(16).width = 22; // Assigned Vehicle

    // Add data validation (dropdowns) starting from row 2
    // Driver Status dropdown (Column F, index 6)
    const statusOptions = ['Active', 'Inactive'];
    for (let row = 2; row <= 1000; row++) {
      worksheet.getCell(row, 6).dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [`"${statusOptions.join(',')}"`],
        showInputMessage: true,
        promptTitle: 'Driver Status',
        prompt: 'Please select a driver status from the dropdown'
      };
    }

    // Assigned Vehicle dropdown (Column P, index 16) - use vehicles
    if (vehicles && vehicles.length > 0) {
      const vehicleOptions = vehicles
        .filter(v => v.vehicle_number || v.vehicleNumber)
        .map(vehicle => vehicle.vehicle_number || vehicle.vehicleNumber || '')
        .filter(Boolean);
      
      if (vehicleOptions.length > 0) {
        const vehicleListStr = vehicleOptions.join(',');
        if (vehicleListStr.length <= 255) {
          for (let row = 2; row <= 1000; row++) {
            worksheet.getCell(row, 16).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [`"${vehicleListStr}"`],
              showInputMessage: true,
              promptTitle: 'Assigned Vehicle',
              prompt: 'Please select a vehicle from the dropdown (optional)'
            };
          }
        }
      }
    }

    // Add date format validation for date columns
    const dateColumns = [3, 7, 13, 14];
    const dateColumnNames = [
      'Date of Birth',
      'Joining Date',
      'Driving License Expiry Date',
      'Medical Certificate Expiry Date'
    ];

    dateColumns.forEach((colIndex, idx) => {
      worksheet.getColumn(colIndex).numFmt = 'dd/mm/yyyy';
      
      for (let row = 2; row <= 1000; row++) {
        worksheet.getCell(row, colIndex).dataValidation = {
          type: 'custom',
          allowBlank: idx === 0 || idx === 1 ? false : true,
          formula1: 'TRUE',
          showInputMessage: true,
          promptTitle: dateColumnNames[idx],
          prompt: 'Enter date in DD/MM/YYYY format (e.g., 31/12/2025). You can also use YYYY-MM-DD or DD-MM-YYYY.'
        };
      }
    });

    // Generate Excel file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Driver_Import_Sample.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    setShowSampleCSVModal(true);
  };

  const handleProceedWithImport = () => {
    setShowSampleCSVModal(false);
    document.getElementById('import-file-input').click();
  };

  // Export drivers to Excel with all fields and driver document details
  const handleExport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Drivers Export');

      // Define headers with all driver fields and document details
      const headers = [
        'Driver ID',
        'Full Name',
        'Mobile Number',
        'Date of Birth',
        'Address',
        'Alternate Number',
        'Driver Status',
        'Joining Date',
        'License Number',
        'License Type',
        'Driving License Document Number',
        'Driving License Expiry Date',
        'Aadhar Card Document Number',
        'PAN Card Document Number',
        'Medical Certificate Document Number',
        'Medical Certificate Expiry Date',
        'Transporter',
        'Assigned Vehicle'
      ];

      // Add header row
      worksheet.addRow(headers);

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      // Add data rows
      for (const driver of drivers) {
        const docs = driver.documents || {};
        const drivingLicense = docs.drivingLicense || {};
        const aadharCard = docs.aadharCard || {};
        const panCard = docs.panCard || {};
        const medicalCertificate = docs.medicalCertificate || {};

        // Format dates
        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toISOString().split('T')[0]; // YYYY-MM-DD format
          } catch (e) {
            return dateStr;
          }
        };

        // Get license number from driver_documents first, fallback to driver.licenseNumber
        const licenseNumber = drivingLicense.document_number || 
                             drivingLicense.documentNumber || 
                             driver.licenseNumber || '';

        const row = [
          driver.id || '',
          driver.fullName || '',
          driver.mobileNumber || '',
          formatDate(driver.dateOfBirth || ''),
          driver.address || '',
          driver.alternateNumber || '',
          driver.status || '',
          formatDate(driver.joiningDate || ''),
          licenseNumber,
          driver.licenseType || '',
          drivingLicense.document_number || drivingLicense.documentNumber || '',
          formatDate(drivingLicense.expiry_date || drivingLicense.date || drivingLicense.validTill || ''),
          aadharCard.document_number || aadharCard.documentNumber || '',
          panCard.document_number || panCard.documentNumber || '',
          medicalCertificate.document_number || medicalCertificate.documentNumber || '',
          formatDate(medicalCertificate.expiry_date || medicalCertificate.date || medicalCertificate.validTill || ''),
          driver.agency || '',
          driver.assignedVehicle || 'Not Assigned'
        ];
        worksheet.addRow(row);
      }

      // Set column widths
      worksheet.getColumn(1).width = 12;  // Driver ID
      worksheet.getColumn(2).width = 20;  // Full Name
      worksheet.getColumn(3).width = 18;  // Mobile Number
      worksheet.getColumn(4).width = 18;  // Date of Birth
      worksheet.getColumn(5).width = 35;  // Address
      worksheet.getColumn(6).width = 18;  // Alternate Number
      worksheet.getColumn(7).width = 15;  // Driver Status
      worksheet.getColumn(8).width = 18;  // Joining Date
      worksheet.getColumn(9).width = 20;  // License Number
      worksheet.getColumn(10).width = 15; // License Type
      worksheet.getColumn(11).width = 30; // Driving License Document Number
      worksheet.getColumn(12).width = 28; // Driving License Expiry Date
      worksheet.getColumn(13).width = 30; // Aadhar Card Document Number
      worksheet.getColumn(14).width = 30; // PAN Card Document Number
      worksheet.getColumn(15).width = 35; // Medical Certificate Document Number
      worksheet.getColumn(16).width = 30; // Medical Certificate Expiry Date
      worksheet.getColumn(17).width = 25; // Transporter
      worksheet.getColumn(18).width = 22; // Assigned Vehicle

      // Generate Excel file and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Drivers_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting drivers:', err);
      alert('Failed to export drivers: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEditClick = (driver) => {
    setEditingDriver(driver);
    // Helper to normalise various API date formats to yyyy-mm-dd for inputs
    const fmt = (val) => {
      if (!val) return '';
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().slice(0,10);
      } catch (e) { return ''; }
    };

    // Determine assigned vehicle id from multiple possible shapes
    const assignedVehId = driver.assignedVehicleId || driver.assigned_vehicle_id || driver.vehicle_id || (driver.assigned_vehicle && driver.assigned_vehicle.id) || null;

    // Get agency name from localStorage or use driver's agency name
    let agencyName = driver.agency || '';
    if (!agencyName) {
      const savedAgency = localStorage.getItem('agencyProfile');
      if (savedAgency) {
        try {
          const agency = JSON.parse(savedAgency);
          agencyName = agency.name || agency.agency_name || agency.company_name || '';
        } catch (error) {
          console.error('Error loading agency name:', error);
        }
      }
    }

    // Initialize form with driver data (normalise dates)
    setFormData({
      fullName: driver.fullName || driver.full_name || '',
      mobileNumber: driver.mobileNumber || driver.mobile_number || '',
      dateOfBirth: fmt(driver.dateOfBirth || driver.date_of_birth || driver.dob || ''),
      address: driver.address || '',
      alternateNumber: driver.alternateNumber || driver.alternate_number || '',
      driverStatus: driver.status || driver.driver_status || 'Active',
      joiningDate: fmt(driver.joiningDate || driver.joining_date || ''),
      licenseNumber: driver.licenseNumber || driver.license_number || '',
      licenseType: driver.licenseType || driver.license_type || '',
      aadharNumber: driver.aadharNumber || driver.aadhar_number || '',
      panNumber: driver.panNumber || driver.pan_number || '',
      medicalCertificate: driver.medicalCertificate || driver.medical_certificate_number || driver.medical_certificate || '',
      agencyId: driver.agencyId || driver.agency_id || currentAgencyId || null,
      agencyName: agencyName,
      assignedVehicleId: assignedVehId,
      photo: null,
      photoPreview: driver.photo || driver.photoUrl || driver.photo_url || null
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full Name is required';
    }
    
    if (!formData.mobileNumber.trim()) {
      errors.mobileNumber = 'Mobile Number is required';
    }
    
    if (!formData.dateOfBirth) {
      errors.dateOfBirth = 'Date of Birth is required';
    }
    
    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }
    
    if (!formData.driverStatus) {
      errors.driverStatus = 'Driver Status is required';
    }
    
    if (!formData.joiningDate) {
      errors.joiningDate = 'Joining Date is required';
    }
    
    // License & ID Details fields are optional
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validateForm()) {
      return;
    }
    setSaveConfirmType('add');
    setShowSaveConfirm(true);
  };

  const handleUpdateClick = () => {
    if (!validateForm()) {
      return;
    }
    setSaveConfirmType('update');
    setShowSaveConfirm(true);
  };

  // Map component format (camelCase) to API format (snake_case)
  const mapDriverToAPI = (driver) => {
    return {
      // allow passing explicit driver id/code when creating
      driver_id: driver.driverId || driver.driver_id || undefined,
      full_name: driver.fullName,
      mobile_number: driver.mobileNumber,
      alternate_number: driver.alternateNumber || null,
      date_of_birth: driver.dateOfBirth || null,
      address: driver.address || null,
      license_number: driver.licenseNumber || null,
      license_type: driver.licenseType || null,
      aadhar_number: driver.aadharNumber || null,
      pan_number: driver.panNumber || null,
      joining_date: driver.joiningDate || null,
      agency_id: driver.agencyId || null,
      assigned_vehicle_id: driver.assignedVehicleId || null,
      medical_certificate_number: driver.medicalCertificate || null,
      driver_status: driver.driverStatus || 'Active',
      photo: driver.photo || null // File object if present
    };
  };

  const handleSaveConfirm = async () => {
    try {
      if (saveConfirmType === 'add') {
        // Prepare driver data for API
        const driverData = mapDriverToAPI({
          fullName: formData.fullName,
          mobileNumber: formData.mobileNumber,
          alternateNumber: formData.alternateNumber,
          dateOfBirth: formData.dateOfBirth,
          address: formData.address,
          licenseNumber: formData.licenseNumber,
          licenseType: formData.licenseType,
          aadharNumber: formData.aadharNumber,
          panNumber: formData.panNumber,
          medicalCertificate: formData.medicalCertificate,
          joiningDate: formData.joiningDate,
          agencyId: currentAgencyId || formData.agencyId || null,
          assignedVehicleId: formData.assignedVehicleId || null,
          driverStatus: formData.driverStatus,
          photo: formData.photo || null
        });

        // Create driver via API
        const response = await createDriver(driverData);
        // API response structure typically: { success, message, data }
        const body = response.data || response || {};
        if (body.success === false) {
          throw new Error(body.message || 'Failed to create driver');
        }
        const created = body.data || body;
        setSuccessDriverId(created?.driver_id || created?.id || '');
        setSuccessDriverName(formData.fullName);
        setSuccessModalType('add');
        handleCloseModal();
        setShowSaveConfirm(false);
        setShowSuccessModal(true);
        // Refresh driver list
        await fetchDrivers();
      } else if (saveConfirmType === 'update') {
        // Prepare payload for update
        const updData = mapDriverToAPI({
          fullName: formData.fullName,
          mobileNumber: formData.mobileNumber,
          alternateNumber: formData.alternateNumber,
          dateOfBirth: formData.dateOfBirth,
          address: formData.address,
          licenseNumber: formData.licenseNumber,
          licenseType: formData.licenseType,
          aadharNumber: formData.aadharNumber,
          panNumber: formData.panNumber,
          medicalCertificate: formData.medicalCertificate,
          joiningDate: formData.joiningDate,
          agencyId: currentAgencyId || formData.agencyId || editingDriver?.agencyId || null,
          assignedVehicleId: formData.assignedVehicleId || editingDriver?.assignedVehicleId || null,
          driverStatus: formData.driverStatus,
          photo: formData.photo || null
        });

        try {
          const res = await updateDriver(editingDriver.id, updData);
          const body2 = res.data || res || {};
          if (body2.success === false) throw new Error(body2.message || 'Failed to update driver');
        } catch (uerr) {
          throw uerr;
        }
        await fetchDrivers();
        setSuccessDriverId(editingDriver.id);
        setSuccessDriverName(formData.fullName);
        setSuccessModalType('update');
        handleCloseModal();
        setShowSaveConfirm(false);
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Error saving driver:', err);
      setError(err.message || 'Failed to save driver. Please try again.');
      setShowSaveConfirm(false);
      alert(err.message || 'Failed to save driver. Please try again.');
    }
  };

  const handleSaveCancel = () => {
    setShowSaveConfirm(false);
    setSaveConfirmType(null);
  };

  const handleDeleteClick = (driver) => {
    setDriverToDelete(driver);
    setShowDeleteDriverConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (driverToDelete) {
      try {
        const driverIdToDelete = driverToDelete.id;
        await deleteDriver(driverIdToDelete);
        
        // Store deleted driver info for success modal
        setDeletedDriverInfo({
          id: driverToDelete.id,
          name: driverToDelete.fullName
        });
        
        // Refresh drivers list
        await fetchDrivers();
        
        // Close confirmation and show success
        setShowDeleteDriverConfirm(false);
        setDriverToDelete(null);
        setShowDeleteSuccess(true);
      } catch (err) {
        console.error('Error deleting driver:', err);
        alert(err.message || 'Failed to delete driver. Please try again.');
        setShowDeleteDriverConfirm(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDriverConfirm(false);
    setDriverToDelete(null);
  };

  const handleUploadClick = async (driver) => {
    setSelectedDriverForDocs(driver);
    setActiveDocTab('drivingLicense'); // Reset to first tab
    // Try to fetch latest documents for this driver (preferred)
    try {
      const driverId = driver?.id || driver?.driverCode || null;
      if (driverId) {
        const docs = await getDriverDocuments(driverId);
        if (docs && Array.isArray(docs) && docs.length > 0) {
          const mappedDocs = {};
          const loadedDetails = {};
          const loadedPreviews = {};
          const loadedFileNames = {};
          const originalFileNames = {};
          docs.forEach((d) => {
            const key = Object.keys(documentDetails).find(k => getDocumentLabel(k).toLowerCase() === (d.document_type || '').toLowerCase()) || null;
            if (!key) return;
            const preview = d.document_url || d.url || d.preview || null;
            const fileName = d.file_name || d.fileName || (preview ? String(preview).split('/').pop() : null) || null;
            mappedDocs[key] = {
              id: d.id || d.document_id || null,
              documentNumber: d.document_number || d.documentNumber || '',
              fileName,
              preview: preview ? encodeURI(preview) : null,
              date: d.expiry_date || d.date || d.validTill || '',
              updatedAt: d.updated_at || d.updatedAt || null
            };
            // Format expiry date for date input (YYYY-MM-DD format)
            const expiryDateValue = mappedDocs[key].date || '';
            let formattedExpiryDate = '';
            if (expiryDateValue) {
              try {
                const dateObj = new Date(expiryDateValue);
                if (!isNaN(dateObj.getTime())) {
                  const year = dateObj.getFullYear();
                  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                  const day = String(dateObj.getDate()).padStart(2, '0');
                  formattedExpiryDate = `${year}-${month}-${day}`;
                }
              } catch (e) {
                console.warn('Error formatting expiry date:', e);
              }
            }
            // Format updated_at for display
            let formattedLastUpdated = '';
            if (mappedDocs[key].updatedAt) {
              try {
                const updatedDate = new Date(mappedDocs[key].updatedAt);
                if (!isNaN(updatedDate.getTime())) {
                  formattedLastUpdated = updatedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                }
              } catch (e) {
                console.warn('Error formatting updated_at:', e);
              }
            }
            loadedDetails[key] = {
              expiryDate: formattedExpiryDate,
              status: mappedDocs[key].fileName ? 'Valid' : 'Missing',
              lastUpdated: formattedLastUpdated,
              documentNumber: mappedDocs[key].documentNumber || ''
            };
            loadedPreviews[key] = mappedDocs[key].preview || null;
            loadedFileNames[key] = mappedDocs[key].fileName || null;
            if (mappedDocs[key].fileName) originalFileNames[key] = mappedDocs[key].fileName;
          });
          Object.keys(documentDetails).forEach(k => {
            if (!mappedDocs[k]) {
              mappedDocs[k] = { id: null, documentNumber: '', fileName: null, preview: null, date: '' };
              if (!loadedDetails[k]) {
                loadedDetails[k] = { expiryDate: '', status: 'Missing', lastUpdated: '' };
              }
            }
          });
          setDocumentDetails(loadedDetails);
          setDocumentPreviews(loadedPreviews);
          setDocumentFileNames(loadedFileNames);
          setOriginalDocumentFileNames(originalFileNames);
          setSelectedDriverForDocs(prev => ({ ...prev, documents: mappedDocs }));
          setShowDocumentsModal(true);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch driver documents on open, falling back to driver object:', err);
    }
    // Load document details from driver if available
    if (driver.documents) {
      const loadedDetails = {};
      const loadedPreviews = {};
      const loadedFileNames = {};
      const originalFileNames = {};
      Object.keys(documentDetails).forEach(key => {
        // Aadhar and PAN cards don't have expiry dates
        const hasExpiryDate = key === 'drivingLicense' || key === 'medicalCertificate';
        const docSource = driver.documents[key] || {};
        const expiryDateRaw = hasExpiryDate ? (docSource.date || docSource.expiry_date || docSource.validTill || '') : '';
        // Format expiry date for date input (YYYY-MM-DD format)
        let formattedExpiryDate = '';
        if (expiryDateRaw) {
          try {
            const dateObj = new Date(expiryDateRaw);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              formattedExpiryDate = `${year}-${month}-${day}`;
            }
          } catch (e) {
            console.warn('Error formatting expiry date:', e);
          }
        }
        // Use updated_at for lastUpdated, not expiry date
        const updatedAtRaw = docSource.updated_at || docSource.updatedAt || docSource.lastUpdated || '';
        let formattedLastUpdated = '';
        if (updatedAtRaw) {
          try {
            const updatedDate = new Date(updatedAtRaw);
            if (!isNaN(updatedDate.getTime())) {
              formattedLastUpdated = updatedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            }
          } catch (e) {
            console.warn('Error formatting updated_at:', e);
          }
        }
        const documentNumberValue = docSource.documentNumber || docSource.document_number || '';
        loadedDetails[key] = {
          expiryDate: formattedExpiryDate,
          status: docSource.fileName || docSource.file_name || docSource.document_url ? 'Valid' : 'Missing',
          lastUpdated: formattedLastUpdated,
          documentNumber: documentNumberValue
        };
        // Load preview and file name if available (support multiple possible API shapes)
        const previewUrl = docSource.preview || docSource.document_url || docSource.url || null;
        const fileNameVal = docSource.fileName || docSource.file_name || (previewUrl ? previewUrl.split('/').pop() : null) || null;
        if (previewUrl) {
          loadedPreviews[key] = previewUrl;
        }
        if (fileNameVal) {
          loadedFileNames[key] = fileNameVal;
          originalFileNames[key] = fileNameVal;
        }
      });
      setDocumentDetails(loadedDetails);
      setDocumentPreviews(loadedPreviews);
      setDocumentFileNames(loadedFileNames);
      setOriginalDocumentFileNames(originalFileNames);
    } else {
      // Reset to defaults if no documents
      setDocumentDetails({
        drivingLicense: { expiryDate: '', status: 'Missing', lastUpdated: '', documentNumber: '' },
        aadharCard: { expiryDate: '', status: 'Missing', lastUpdated: '', documentNumber: '' },
        panCard: { expiryDate: '', status: 'Missing', lastUpdated: '', documentNumber: '' },
        medicalCertificate: { expiryDate: '', status: 'Missing', lastUpdated: '', documentNumber: '' }
      });
      setDocumentPreviews({
        drivingLicense: null,
        aadharCard: null,
        panCard: null,
        medicalCertificate: null
      });
      setDocumentFileNames({
        drivingLicense: null,
        aadharCard: null,
        panCard: null,
        medicalCertificate: null
      });
      setOriginalDocumentFileNames({});
    }
    setShowDocumentsModal(true);
  };

  const handleDocumentReplace = (docKey) => {
    const inputId = `driver-doc-replace-${docKey}`;
    document.getElementById(inputId).click();
  };

  const handleDocumentFileChange = (docKey, file) => {
    if (file) {
      // Store file name
      setDocumentFileNames(prev => ({
        ...prev,
        [docKey]: file.name
      }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setDocumentPreviews(prev => ({
            ...prev,
            [docKey]: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
      // Keep file object for upload
      setDocumentFiles(prev => ({
        ...prev,
        [docKey]: file
      }));
      
      // Update last updated date
      setDocumentDetails(prev => ({
        ...prev,
        [docKey]: {
          ...prev[docKey],
          status: 'Valid',
          lastUpdated: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        }
      }));
    }
  };

  const handleDocumentDownload = (docKey) => {
    // Placeholder for download functionality
    alert(`Downloading ${getDocumentLabel(docKey)} document...`);
  };

  const getDocumentLabel = (key) => {
    const labels = {
      drivingLicense: 'Driving License',
      aadharCard: 'Aadhar Card',
      panCard: 'PAN Card',
      medicalCertificate: 'Medical Certificate'
    };
    return labels[key] || key;
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleDocumentSave = () => {
    (async () => {
      try {
        const changedKeys = Object.keys(documentDetails).filter((key) => {
          const existingDoc = selectedDriverForDocs?.documents?.[key] || {};
          const hasNewFile = Boolean(documentFiles[key]);
          const fileNameChanged = documentFileNames[key] && documentFileNames[key] !== existingDoc.fileName;
          const expiryChanged = (documentDetails[key]?.expiryDate || '') !== (existingDoc.date || '');
          const numberChanged = (documentDetails[key]?.documentNumber || '') !== (existingDoc.documentNumber || '');
          return hasNewFile || fileNameChanged || expiryChanged || numberChanged;
        });

        if (changedKeys.length === 0) {
          setShowDocumentSaveConfirm(false);
          setShowDocumentsModal(false);
          setShowDocumentSuccess(true);
          return;
        }

        const docsPayload = changedKeys.map((key) => {
          const existingDoc = selectedDriverForDocs?.documents?.[key] || {};
          return {
            id: existingDoc?.id || null,
            document_type: getDocumentLabel(key),
            document_number: documentDetails[key]?.documentNumber || existingDoc.documentNumber || '',
            expiry_date: documentDetails[key]?.expiryDate || existingDoc.date || '',
            file_name: documentFileNames[key] || existingDoc.fileName || null,
            file: documentFiles[key] || null
          };
        });

        const driverId = selectedDriverForDocs?.id || selectedDriverForDocs?.driverCode || null;
        if (!driverId) throw new Error('Missing driver id for uploading documents');

        const { createDriverDocument, updateDriverDocument, getDriverDocuments: fetchDocs } = await import('../../services/driverDocumentsService');
        const uploadPromises = docsPayload.map(async (doc) => {
          const form = new FormData();
          if (doc.document_type) form.append('document_type', doc.document_type);
          if (doc.document_number) form.append('document_number', doc.document_number);
          if (doc.expiry_date) {
            const d = doc.expiry_date;
            form.append('expiry_date', typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d).toISOString() : d);
          }
          if (doc.file_name) form.append('file_name', doc.file_name);
          if (doc.file && doc.file instanceof File) form.append('file', doc.file);
          if (!doc.id) form.append('driver_id', driverId);
          if (doc.id) {
            return updateDriverDocument(doc.id, form);
          } else {
            return createDriverDocument(form);
          }
        });
        await Promise.all(uploadPromises);

        // Refresh docs and update UI
        const savedDocs = await fetchDocs(driverId);
        const docsByType = {};
        if (Array.isArray(savedDocs)) {
        savedDocs.forEach(d => {
            const key = Object.keys(documentDetails).find(k => getDocumentLabel(k).toLowerCase() === (d.document_type || '').toLowerCase()) || null;
            if (!key) return;
            const preview = d.document_url || d.url || d.preview || null;
            const fileName = d.file_name || d.fileName || (preview ? String(preview).split('/').pop() : null) || null;
            docsByType[key] = {
              id: d.id || d.document_id || null,
              fileName,
              preview,
              date: d.expiry_date || d.date || d.validTill || '',
              documentNumber: d.document_number || d.documentNumber || '',
              lastUpdated: d.updated_at || d.lastUpdated || ''
            };
          });
        }
        setSelectedDriverForDocs(prev => ({ ...prev, documents: { ...(prev.documents || {}), ...docsByType } }));

        const newFileNames = {};
        const newOriginalFileNames = {};
        const newPreviews = {};
        const newDetails = { ...documentDetails };
        Object.keys(documentDetails).forEach(key => {
          const existing = docsByType[key] || selectedDriverForDocs?.documents?.[key] || {};
          newFileNames[key] = existing.fileName || documentFileNames[key] || null;
          newOriginalFileNames[key] = existing.fileName || null;
          newPreviews[key] = existing.preview || documentPreviews[key] || null;
          
          // Format expiry date for date input (YYYY-MM-DD format)
          const expiryDateRaw = existing.date || newDetails[key]?.expiryDate || '';
          let formattedExpiryDate = '';
          if (expiryDateRaw) {
            try {
              const dateObj = new Date(expiryDateRaw);
              if (!isNaN(dateObj.getTime())) {
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                formattedExpiryDate = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.warn('Error formatting expiry date:', e);
            }
          }
          
          // Format lastUpdated from updated_at
          const updatedAtRaw = existing.lastUpdated || existing.updatedAt || newDetails[key]?.lastUpdated || '';
          let formattedLastUpdated = '';
          if (updatedAtRaw) {
            try {
              const updatedDate = new Date(updatedAtRaw);
              if (!isNaN(updatedDate.getTime())) {
                formattedLastUpdated = updatedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
              } else {
                // If it's already a formatted string, use it as is
                formattedLastUpdated = updatedAtRaw;
              }
            } catch (e) {
              // If it's already a formatted string, use it as is
              formattedLastUpdated = updatedAtRaw;
            }
          }
          
          newDetails[key] = {
            ...newDetails[key],
            expiryDate: formattedExpiryDate,
            status: existing.fileName ? 'Valid' : newDetails[key]?.status || 'Missing',
            lastUpdated: formattedLastUpdated,
            documentNumber: existing.documentNumber || newDetails[key]?.documentNumber || ''
          };
        });
        setDocumentFileNames(newFileNames);
        setOriginalDocumentFileNames(newOriginalFileNames);
        setDocumentPreviews(newPreviews);
        setDocumentDetails(newDetails);

        setShowDocumentSaveConfirm(false);
        setShowDocumentsModal(false);
        setShowDocumentSuccess(true);
      } catch (err) {
        console.error('Error saving driver documents (agency):', err);
        alert(err.message || 'Failed to save documents. Please try again.');
        setShowDocumentSaveConfirm(false);
      }
    })();
  };

  const handleDocumentSaveConfirm = () => {
    handleDocumentSave();
  };

  const handleDocumentSaveCancel = () => {
    setShowDocumentSaveConfirm(false);
    setUploadedDocumentNames([]);
  };

  return (
    <div className="driver-management-container">
      {/* Header Section */}
      <div className="driver-header">
        <div className="driver-filters">
          <div className="filter-group">
            <div className="filter-wrapper" ref={statusDropdownRef}>
              <button 
                className={`filter-btn ${statusFilter ? 'active' : ''}`}
                onClick={() => {
                  setStatusDropdownOpen(!statusDropdownOpen);
                  setDocumentDropdownOpen(false);
                }}
              >
                <span>▼</span> Filter by Status {statusFilter && `(${statusFilter})`}
              </button>
              {statusDropdownOpen && (
                <div className="filter-dropdown">
                  <button
                    className={`filter-option ${statusFilter === '' ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter('');
                      setStatusDropdownOpen(false);
                    }}
                  >
                    All
                  </button>
                  <button
                    className={`filter-option ${statusFilter === 'Active' ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter('Active');
                      setStatusDropdownOpen(false);
                    }}
                  >
                    Active
                  </button>
                  <button
                    className={`filter-option ${statusFilter === 'Inactive' ? 'selected' : ''}`}
                    onClick={() => {
                      setStatusFilter('Inactive');
                      setStatusDropdownOpen(false);
                    }}
                  >
                    Inactive
                  </button>
                </div>
              )}
            </div>
            <div className="filter-wrapper" ref={documentDropdownRef}>
              <button 
                className={`filter-btn ${documentValidityFilter ? 'active' : ''}`}
                onClick={() => {
                  setDocumentDropdownOpen(!documentDropdownOpen);
                  setStatusDropdownOpen(false);
                }}
              >
                <span>▼</span> Document Validity {documentValidityFilter && `(${documentValidityFilter})`}
              </button>
              {documentDropdownOpen && (
                <div className="filter-dropdown">
                  <button
                    className={`filter-option ${documentValidityFilter === '' ? 'selected' : ''}`}
                    onClick={() => {
                      setDocumentValidityFilter('');
                      setDocumentDropdownOpen(false);
                    }}
                  >
                    All
                  </button>
                  <button
                    className={`filter-option ${documentValidityFilter === 'Valid' ? 'selected' : ''}`}
                    onClick={() => {
                      setDocumentValidityFilter('Valid');
                      setDocumentDropdownOpen(false);
                    }}
                  >
                    Valid
                  </button>
                  <button
                    className={`filter-option ${documentValidityFilter === 'Expire Soon' ? 'selected' : ''}`}
                    onClick={() => {
                      setDocumentValidityFilter('Expire Soon');
                      setDocumentDropdownOpen(false);
                    }}
                  >
                    Expire Soon
                  </button>
                  <button
                    className={`filter-option ${documentValidityFilter === 'Expired' ? 'selected' : ''}`}
                    onClick={() => {
                      setDocumentValidityFilter('Expired');
                      setDocumentDropdownOpen(false);
                    }}
                  >
                    Expired
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="driver-actions">
          <button className="btn-primary" onClick={handleAddDriver}>
            <span>+</span> Add Driver
          </button>
          <button className="btn-icon-import" onClick={handleImportClick} title="Import">
            <span>📥</span>
          </button>
          <input
            id="import-file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleImportFileChange}
          />
          <button className="btn-icon-export" onClick={handleExport} title="Export">
            <span>📤</span>
          </button>
        </div>
      </div>

      {/* Show Entries Section */}
      <div className="show-entries-container">
        <div className="show-entries-wrapper">
          <div className="show-entries-left">
            <span className="show-entries-label">Show</span>
            <div className="filter-wrapper" ref={showEntriesDropdownRef}>
              <button
                className="show-entries-btn"
                onClick={() => {
                  setShowEntriesDropdownOpen(!showEntriesDropdownOpen);
                  setStatusDropdownOpen(false);
                  setDocumentDropdownOpen(false);
                }}
              >
                <span>{itemsPerPage}</span>
                <span>▼</span>
              </button>
              {showEntriesDropdownOpen && (
                <div className="filter-dropdown">
                  {[10, 25, 50, 100, 500].map(num => (
                    <button
                      key={num}
                      className={`filter-option ${itemsPerPage === num ? 'selected' : ''}`}
                      onClick={() => {
                        setItemsPerPage(num);
                        setShowEntriesDropdownOpen(false);
                        setCurrentPage(1);
                      }}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="show-entries-label">entries</span>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, mobile or licens..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="driver-table-container">
        <table className="driver-table">
          <thead>
            <tr>
              <th>Driver ID</th>
              <th>Full Name</th>
              <th>Mobile Number</th>
              <th>License Number</th>
              <th>Transporter</th>
              <th>Status</th>
              <th>Assigned Vehicle</th>
              <th>Documents</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentDrivers.map((driver) => (
              <tr key={driver.id}>
                <td>{driver.id}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1rem' }}>👤</span>
                    {driver.fullName}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1rem' }}>📞</span>
                    {driver.mobileNumber}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1rem' }}>📄</span>
                    <div>
                      {driver.documents?.drivingLicense?.document_number || 
                       driver.documents?.drivingLicense?.documentNumber || 
                       driver.licenseNumber || 
                       '-'}
                    </div>
                  </div>
                </td>
                <td>{driver.agency}</td>
                <td>
                  <span className={getStatusBadgeClass(driver.status)}>
                    {driver.status}
                  </span>
                </td>
                <td>{driver.assignedVehicle}</td>
                <td>
                  {(() => {
                    const docExpiry = driver.documentsExpiry;
                    return (
                      <div 
                        className="doc-expiry-cell" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleUploadClick(driver)}
                        title="Click to view documents"
                      >
                        <div className={getDocumentProgressClass(docExpiry)}>
                          <div
                            className="doc-progress-bar"
                            style={{ width: `${getProgressPercentage(docExpiry)}%` }}
                          ></div>
                        </div>
                        <div className="doc-expiry-info">
                          <span>{docExpiry.total} Total</span>
                          <span style={{ color: '#10b981' }}>
                            ✓ {docExpiry.valid} Valid
                          </span>
                          <span style={{ color: '#ef4444' }}>
                            ✗ {docExpiry.expired} Expired
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit-btn" title="Edit" onClick={() => handleEditClick(driver)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.333 3.333L12.667 4.667M10.667 2L14 5.333L5.333 14H2V10.667L10.667 2Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    <button className="action-btn delete-btn" title="Delete" onClick={() => handleDeleteClick(driver)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="4" y="5.5" width="8" height="8.5" rx="0.5" stroke="#dc2626" strokeWidth="1.25" fill="none"/>
                        <path d="M2 5.5h12M6 5.5V4.25C6 3.83579 6.33579 3.5 6.75 3.5h2.5C9.66421 3.5 10 3.83579 10 4.25V5.5" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="6.5" y1="8.5" x2="6.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="9.5" y1="8.5" x2="9.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <button className="action-btn import-btn" title="Import" onClick={() => handleUploadClick(driver)}>
                      📥
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Section */}
      <div className="driver-pagination">
        <div className="pagination-info">
          Showing {currentDrivers.length > 0 ? startIndex + 1 : 0} to {endIndex} of {totalCount} results
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ←
          </button>
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            →
          </button>
        </div>
      </div>

      {/* Add Driver Modal */}
      {showAddModal && (
        <div className="add-vehicle-modal-overlay" onClick={handleCloseModal}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Add New Driver</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <div className="modal-body-content">
              <div className="form-content">
                {/* Left Column - Form Fields */}
                <div className="form-fields-column">
                  
                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Full Name <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-field ${formErrors.fullName ? 'error' : ''}`}
                      placeholder="Enter full name"
                      value={formData.fullName}
                      onChange={(e) => {
                        setFormData({...formData, fullName: e.target.value});
                        if (formErrors.fullName) {
                          setFormErrors({...formErrors, fullName: ''});
                        }
                      }}
                    />
                    {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📞</span> Mobile Number <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`form-input-field ${formErrors.mobileNumber ? 'error' : ''}`}
                      placeholder="Enter mobile number"
                      value={formData.mobileNumber}
                      onChange={(e) => {
                        setFormData({...formData, mobileNumber: e.target.value});
                        if (formErrors.mobileNumber) {
                          setFormErrors({...formErrors, mobileNumber: ''});
                        }
                      }}
                    />
                    {formErrors.mobileNumber && <span className="error-message">{formErrors.mobileNumber}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📅</span> Date of Birth <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-input-field ${formErrors.dateOfBirth ? 'error' : ''}`}
                      placeholder="yyyy/mm/dd"
                      value={formData.dateOfBirth}
                      onChange={(e) => {
                        setFormData({...formData, dateOfBirth: e.target.value});
                        if (formErrors.dateOfBirth) {
                          setFormErrors({...formErrors, dateOfBirth: ''});
                        }
                      }}
                    />
                    {formErrors.dateOfBirth && <span className="error-message">{formErrors.dateOfBirth}</span>}
                  </div>
                </div>

                {/* Right Column - Photo Upload */}
                <div className="photo-upload-column">
                  <div className="photo-upload-box">
                    {formData.photoPreview ? (
                      <>
                        <img src={formData.photoPreview} alt="Driver preview" className="photo-preview" />
                        <button
                          type="button"
                          className="photo-remove-btn"
                          onClick={() => {
                            setFormData({...formData, photo: null, photoPreview: null});
                            document.getElementById('add-driver-photo-input').value = '';
                          }}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="photo-upload-icon">📷</div>
                        <p className="photo-upload-text">Upload Photo</p>
                        <p className="photo-upload-hint">JPG or PNG (max 5MB)</p>
                      </>
                    )}
                    <input
                      type="file"
                      id="add-driver-photo-input"
                      accept="image/jpeg,image/png"
                      className="photo-upload-input"
                      onChange={handlePhotoUpload}
                    />
                  </div>
                </div>
              </div>

              {/* Address full-width below photo */}
              <div className="personal-wide-section">
                <div className="form-field">
                  <label className="form-field-label">
                    <span>🏠</span> Address <span className="required-asterisk">*</span>
                  </label>
                  <textarea
                    className={`form-input-field ${formErrors.address ? 'error' : ''}`}
                    placeholder="Enter address"
                    rows="3"
                    value={formData.address}
                    onChange={(e) => {
                      setFormData({...formData, address: e.target.value});
                      if (formErrors.address) {
                        setFormErrors({...formErrors, address: ''});
                      }
                    }}
                    style={{ resize: 'vertical', minHeight: '80px' }}
                  />
                  {formErrors.address && <span className="error-message">{formErrors.address}</span>}
                </div>
              </div>

              {/* Personal grid row for contact/status/agency/joining date */}
              <div className="personal-grid">
                <div className="form-field">
                  <label className="form-field-label">
                    <span>📞</span> Alternate Number
                  </label>
                  <input
                    type="tel"
                    className="form-input-field"
                    placeholder="Enter alternate number"
                    value={formData.alternateNumber}
                    onChange={(e) => setFormData({...formData, alternateNumber: e.target.value})}
                  />
                </div>

                <div className="form-field">
                  <label className="form-field-label">
                    <span>👤</span> Driver Status <span className="required-asterisk">*</span>
                  </label>
                  <select
                    className={`form-input-field ${formErrors.driverStatus ? 'error' : ''}`}
                    value={formData.driverStatus}
                    onChange={(e) => {
                      setFormData({...formData, driverStatus: e.target.value});
                      if (formErrors.driverStatus) {
                        setFormErrors({...formErrors, driverStatus: ''});
                      }
                    }}
                  >
                    <option value="">Select status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  {formErrors.driverStatus && <span className="error-message">{formErrors.driverStatus}</span>}
                </div>

                <div className="form-field">
                  <label className="form-field-label">
                    <span>🏢</span> Transporter
                  </label>
                  <input
                    type="text"
                    className="form-input-field"
                    value={formData.agencyName || currentAgencyName || ''}
                    readOnly
                    disabled
                    placeholder="Transporter name will be set automatically"
                  />
                </div>

                <div className="form-field">
                  <label className="form-field-label">
                    <span>📅</span> Joining Date <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-input-field ${formErrors.joiningDate ? 'error' : ''}`}
                    placeholder="yyyy/mm/dd"
                    value={formData.joiningDate}
                    onChange={(e) => {
                      setFormData({...formData, joiningDate: e.target.value});
                      if (formErrors.joiningDate) {
                        setFormErrors({...formErrors, joiningDate: ''});
                      }
                    }}
                  />
                  {formErrors.joiningDate && <span className="error-message">{formErrors.joiningDate}</span>}
                </div>
              </div>

            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button className="modal-btn-save" onClick={handleSaveClick}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Driver Modal */}
      {showEditModal && editingDriver && (
        <div className="add-vehicle-modal-overlay" onClick={handleCloseModal}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Edit Driver</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <div className="modal-body-content">
              <div className="form-content">
                {/* Left Column - Form Fields */}
                <div className="form-fields-column">
                  
                  <div className="form-field">
                    <label className="form-field-label">
                      <span>👤</span> Full Name <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-field ${formErrors.fullName ? 'error' : ''}`}
                      placeholder="Enter full name"
                      value={formData.fullName}
                      onChange={(e) => {
                        setFormData({...formData, fullName: e.target.value});
                        if (formErrors.fullName) {
                          setFormErrors({...formErrors, fullName: ''});
                        }
                      }}
                    />
                    {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📞</span> Mobile Number <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`form-input-field ${formErrors.mobileNumber ? 'error' : ''}`}
                      placeholder="Enter mobile number"
                      value={formData.mobileNumber}
                      onChange={(e) => {
                        setFormData({...formData, mobileNumber: e.target.value});
                        if (formErrors.mobileNumber) {
                          setFormErrors({...formErrors, mobileNumber: ''});
                        }
                      }}
                    />
                    {formErrors.mobileNumber && <span className="error-message">{formErrors.mobileNumber}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">
                      <span>📅</span> Date of Birth <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-input-field ${formErrors.dateOfBirth ? 'error' : ''}`}
                      placeholder="yyyy/mm/dd"
                      value={formData.dateOfBirth}
                      onChange={(e) => {
                        setFormData({...formData, dateOfBirth: e.target.value});
                        if (formErrors.dateOfBirth) {
                          setFormErrors({...formErrors, dateOfBirth: ''});
                        }
                      }}
                    />
                    {formErrors.dateOfBirth && <span className="error-message">{formErrors.dateOfBirth}</span>}
                  </div>

                </div>

                {/* Right Column - Photo Upload */}
                <div className="photo-upload-column">
                  <div className="photo-upload-box">
                    {formData.photoPreview ? (
                      <>
                        <img src={formData.photoPreview} alt="Driver preview" className="photo-preview" />
                        <button
                          type="button"
                          className="photo-remove-btn"
                          onClick={() => {
                            setFormData({...formData, photo: null, photoPreview: null});
                            document.getElementById('edit-driver-photo-input').value = '';
                          }}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="photo-upload-icon">📷</div>
                        <p className="photo-upload-text">Upload Photo</p>
                        <p className="photo-upload-hint">JPG or PNG (max 5MB)</p>
                      </>
                    )}
                    <input
                      type="file"
                      id="edit-driver-photo-input"
                      accept="image/jpeg,image/png"
                      className="photo-upload-input"
                      onChange={handlePhotoUpload}
                    />
                  </div>
                </div>
              </div>

              {/* Address full-width below photo */}
              <div className="personal-wide-section">
                <div className="form-field">
                  <label className="form-field-label">
                    <span>🏠</span> Address <span className="required-asterisk">*</span>
                  </label>
                  <textarea
                    className={`form-input-field ${formErrors.address ? 'error' : ''}`}
                    placeholder="Enter address"
                    rows="3"
                    value={formData.address}
                    onChange={(e) => {
                      setFormData({...formData, address: e.target.value});
                      if (formErrors.address) {
                        setFormErrors({...formErrors, address: ''});
                      }
                    }}
                    style={{ resize: 'vertical', minHeight: '80px' }}
                  />
                  {formErrors.address && <span className="error-message">{formErrors.address}</span>}
                </div>
              </div>

              {/* Personal grid row for contact/status/agency/joining date */}
              <div className="personal-grid">
                <div className="form-field">
                  <label className="form-field-label">
                    <span>📞</span> Alternate Number
                  </label>
                  <input
                    type="tel"
                    className="form-input-field"
                    placeholder="Enter alternate number"
                    value={formData.alternateNumber}
                    onChange={(e) => setFormData({...formData, alternateNumber: e.target.value})}
                  />
                </div>

                <div className="form-field">
                  <label className="form-field-label">
                    <span>👤</span> Driver Status <span className="required-asterisk">*</span>
                  </label>
                  <select
                    className={`form-input-field ${formErrors.driverStatus ? 'error' : ''}`}
                    value={formData.driverStatus}
                    onChange={(e) => {
                      setFormData({...formData, driverStatus: e.target.value});
                      if (formErrors.driverStatus) {
                        setFormErrors({...formErrors, driverStatus: ''});
                      }
                    }}
                  >
                    <option value="">Select status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  {formErrors.driverStatus && <span className="error-message">{formErrors.driverStatus}</span>}
                </div>

                <div className="form-field">
                  <label className="form-field-label">
                    <span>🏢</span> Transporter
                  </label>
                  <input
                    type="text"
                    className="form-input-field"
                    value={formData.agencyName || currentAgencyName || ''}
                    readOnly
                    disabled
                    placeholder="Transporter name will be set automatically"
                  />
                </div>

                <div className="form-field">
                  <label className="form-field-label">
                    <span>📅</span> Joining Date <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-input-field ${formErrors.joiningDate ? 'error' : ''}`}
                    placeholder="yyyy/mm/dd"
                    value={formData.joiningDate}
                    onChange={(e) => {
                      setFormData({...formData, joiningDate: e.target.value});
                      if (formErrors.joiningDate) {
                        setFormErrors({...formErrors, joiningDate: ''});
                      }
                    }}
                  />
                  {formErrors.joiningDate && <span className="error-message">{formErrors.joiningDate}</span>}
                </div>
              </div>

            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button className="modal-btn-save" onClick={handleUpdateClick}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save/Update Confirmation Modal */}
      {showSaveConfirm && (
        <div className="modal-overlay" onClick={handleSaveCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm {saveConfirmType === 'add' ? 'Add' : 'Update'}</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to {saveConfirmType === 'add' ? 'add' : 'update'} this driver?</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel-button" onClick={handleSaveCancel}>
                No
              </button>
              <button className="modal-button confirm-button" onClick={handleSaveConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">
              Driver details have been successfully {successModalType === 'add' ? 'Added' : 'Updated'}
            </p>
            <div className="success-id-badge">{successDriverName}</div>
            <button className="success-ok-btn" onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Document Save Confirmation Modal */}
      {showDocumentSaveConfirm && (
        <div className="document-save-confirm-overlay" onClick={handleDocumentSaveCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Save</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to save these document changes?</p>
              {uploadedDocumentNames.length > 0 && (
                <div className="uploaded-docs-list">
                  {uploadedDocumentNames.map((docName, index) => (
                    <span key={index} className="uploaded-doc-badge">{docName}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel-button" onClick={handleDocumentSaveCancel}>
                No
              </button>
              <button className="modal-button confirm-button" onClick={handleDocumentSaveConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Save Success Modal */}
      {showDocumentSuccess && (
        <div className="success-modal-overlay" onClick={() => setShowDocumentSuccess(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">Document has been updated Successfully!</p>
            {uploadedDocumentNames.length > 0 && (
              <div className="uploaded-docs-success-list">
                {uploadedDocumentNames.map((docName, index) => (
                  <span key={index} className="uploaded-doc-success-badge">{docName}</span>
                ))}
              </div>
            )}
            <button className="success-ok-btn" onClick={() => {
              setShowDocumentSuccess(false);
              setUploadedDocumentNames([]);
            }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Delete Driver Confirmation Modal */}
      {showDeleteDriverConfirm && driverToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-vehicle-header">
              <div className="delete-warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-vehicle-title">Delete Driver</h3>
            </div>
            <div className="delete-vehicle-body">
              <p className="delete-vehicle-message">
                Are you sure you want to delete this Driver? This action cannot be undone.
              </p>
              <div className="delete-vehicle-details">
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Driver ID:</span>
                  <span className="delete-detail-value">{driverToDelete.id}</span>
                </div>
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Driver Name:</span>
                  <span className="delete-detail-value">{driverToDelete.fullName}</span>
                </div>
              </div>
            </div>
            <div className="delete-vehicle-footer">
              <button className="delete-vehicle-cancel-btn" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="delete-vehicle-delete-btn" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Driver Success Modal */}
      {showDeleteSuccess && deletedDriverInfo && (
        <div className="success-modal-overlay" onClick={() => setShowDeleteSuccess(false)}>
          <div className="delete-success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-success-header">
              <div className="delete-success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="12" fill="#10B981"/>
                  <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="delete-success-title">Success</span>
              <button className="delete-success-close" onClick={() => setShowDeleteSuccess(false)}>
                ✕
              </button>
            </div>
            <div className="delete-success-body">
              <p className="delete-success-message">Driver successfully deleted</p>
              <div className="delete-success-details">
                <div className="delete-success-detail-row">
                  <span className="delete-success-detail-label">Driver ID:</span>
                  <span className="delete-success-detail-value">{deletedDriverInfo.id}</span>
                </div>
                <div className="delete-success-detail-row">
                  <span className="delete-success-detail-label">Driver Name:</span>
                  <span className="delete-success-detail-value">{deletedDriverInfo.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Driver Documents Modal */}
      {showDocumentsModal && selectedDriverForDocs && (
        <div className="vehicle-documents-modal-overlay" onClick={() => setShowDocumentsModal(false)}>
          <div className="vehicle-documents-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="vehicle-documents-header">
              <div>
                <h2 className="vehicle-documents-title">Driver Documents</h2>
                <p className="vehicle-documents-subtitle">
                  {selectedDriverForDocs.id}: {selectedDriverForDocs.fullName} - {selectedDriverForDocs.licenseType || 'N/A'}
                </p>
              </div>
              <button className="vehicle-documents-close" onClick={() => setShowDocumentsModal(false)}>
                ✕
              </button>
            </div>

            <div className="vehicle-documents-tabs">
              {['drivingLicense', 'aadharCard', 'panCard', 'medicalCertificate'].map((docKey) => (
                <button
                  key={docKey}
                  className={`vehicle-doc-tab ${activeDocTab === docKey ? 'active' : ''}`}
                  onClick={() => setActiveDocTab(docKey)}
                >
                  {getDocumentLabel(docKey)}
                  {documentDetails[docKey]?.status === 'Missing' && <span className="doc-warning-icon">⚠️</span>}
                </button>
              ))}
            </div>

            <div className="vehicle-documents-body">
              <div className="document-preview-section">
                <h3 className="section-title">Document Preview</h3>
                <div className="document-preview-box">
                  {(() => {
                    const preview = documentPreviews[activeDocTab] || selectedDriverForDocs?.documents?.[activeDocTab]?.preview;
                    const fileName = documentFileNames[activeDocTab] || selectedDriverForDocs?.documents?.[activeDocTab]?.fileName || '';
                    
                    if (!preview) {
                      return (
                        <div className="doc-preview-placeholder">
                          <div className="doc-preview-icon">📄</div>
                          <p>No document preview available</p>
                        </div>
                      );
                    }
                    
                    // Check if it's a PDF by file name or URL
                    const isPDF = fileName.toLowerCase().endsWith('.pdf') || 
                                 (typeof preview === 'string' && (preview.toLowerCase().includes('.pdf') || preview.toLowerCase().includes('application/pdf')));
                    
                    if (isPDF) {
                      return (
                        <iframe
                          src={preview}
                          className="doc-preview-image"
                          style={{ width: '100%', height: '100%', border: 'none', minHeight: '400px' }}
                          title="Document preview"
                        />
                      );
                    }
                    
                    // For images or data URLs
                    return (
                      <img 
                        src={preview} 
                        alt="Document preview" 
                        className="doc-preview-image" 
                      />
                    );
                  })()}
                </div>
              </div>

              <div className="document-details-section">
                <h3 className="section-title">Document Details</h3>
                <div className="document-detail-field">
                  <label>Document Number</label>
                  <input
                    type="text"
                    className="document-number-input"
                    placeholder="Enter document number"
                    value={documentDetails[activeDocTab]?.documentNumber || selectedDriverForDocs?.documents?.[activeDocTab]?.documentNumber || ''}
                    onChange={(e) => {
                      setDocumentDetails(prev => ({
                        ...prev,
                        [activeDocTab]: {
                          ...prev[activeDocTab],
                          documentNumber: e.target.value
                        }
                      }));
                    }}
                  />
                </div>
                {/* Only show expiry date for documents that have expiry dates (not Aadhar and PAN) */}
                {(activeDocTab === 'drivingLicense' || activeDocTab === 'medicalCertificate') && (
                  <div className="document-detail-field">
                    <label>Expiry Date</label>
                    <div className="date-input-group">
                      <input
                        type="date"
                        className="document-expiry-input"
                        value={documentDetails[activeDocTab]?.expiryDate || ''}
                        onChange={(e) => {
                          setDocumentDetails(prev => ({
                            ...prev,
                            [activeDocTab]: {
                              ...prev[activeDocTab],
                              expiryDate: e.target.value
                            }
                          }));
                        }}
                      />
                      <span className="calendar-icon">📅</span>
                    </div>
                  </div>
                )}

                <div className="document-detail-field">
                  <label>Status</label>
                  <span className={`doc-status-badge ${documentDetails[activeDocTab]?.status === 'Valid' ? 'valid' : 'missing'}`}>
                    {documentDetails[activeDocTab]?.status || 'Missing'}
                  </span>
                </div>

                <div className="document-detail-field">
                  <label>Document History</label>
                  <input
                    type="text"
                    className="document-history-input"
                    value={`Last updated: ${documentDetails[activeDocTab]?.lastUpdated || 'N/A'}`}
                    readOnly
                  />
                </div>

                <div className="document-actions">
                  <button className="replace-file-btn" onClick={() => handleDocumentReplace(activeDocTab)}>
                    <span>⬆️</span> {(documentPreviews[activeDocTab] || documentFileNames[activeDocTab] || selectedDriverForDocs?.documents?.[activeDocTab]?.fileName || selectedDriverForDocs?.documents?.[activeDocTab]?.preview) ? 'Replace File' : 'Upload File'}
                  </button>
                  <input
                    type="file"
                    id={`driver-doc-replace-${activeDocTab}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    onChange={(e) => handleDocumentFileChange(activeDocTab, e.target.files[0])}
                  />
                  {((documentPreviews[activeDocTab] || documentFileNames[activeDocTab]) || (selectedDriverForDocs?.documents?.[activeDocTab]?.fileName || selectedDriverForDocs?.documents?.[activeDocTab]?.preview)) && (
                    <button className="download-file-btn" onClick={() => handleDocumentDownload(activeDocTab)}>
                      <span>⬇️</span> Download
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="vehicle-documents-footer">
              <button className="close-docs-btn" onClick={() => setShowDocumentsModal(false)}>
                Close
              </button>
              <button className="save-docs-btn" onClick={() => {
                // Get list of all changed/replaced document names
                const changedDocs = [];
                Object.keys(documentFileNames).forEach(key => {
                  const hasNewFile = documentFileNames[key] && documentFileNames[key] !== originalDocumentFileNames[key];
                  const hasNewPreview = documentPreviews[key] && documentPreviews[key] !== (selectedDriverForDocs?.documents?.[key]?.preview || null);
                  if (hasNewFile || hasNewPreview) {
                    changedDocs.push(getDocumentLabel(key));
                  }
                });
                // Always show confirmation before saving
                setUploadedDocumentNames(changedDocs);
                setShowDocumentSaveConfirm(true);
              }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample CSV Modal */}
      {showSampleCSVModal && (
        <div className="add-vehicle-modal-overlay" onClick={() => setShowSampleCSVModal(false)}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Import Drivers</h2>
              <button className="modal-close-btn" onClick={() => setShowSampleCSVModal(false)}>✕</button>
            </div>

            <div className="modal-body-content" style={{ padding: '20px' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '1rem', fontWeight: 600, color: '#333', marginBottom: '8px', display: 'block' }}>Sample Excel Format</label>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px', textAlign: 'left' }}>
                  Download the sample Excel template to see the required format for importing drivers.
                  <br />
                  <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                    Note: Fields marked with * are required. Dropdowns are available for Driver Status and Assigned Vehicle. Driver ID will be auto-generated if not provided.
                  </span>
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button 
                    className="btn-primary" 
                    onClick={handleDownloadSampleCSV}
                    style={{ flex: 1 }}
                  >
                    <span>⬇</span> Download Sample Excel
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={() => setShowSampleCSVModal(false)}>Cancel</button>
              <button className="modal-btn-save" onClick={handleProceedWithImport}>
                Proceed with Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="add-vehicle-modal-overlay" onClick={handleImportCancel}>
          <div className="add-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2 className="modal-title">Import Drivers</h2>
              <button className="modal-close-btn" onClick={handleImportCancel}>✕</button>
            </div>

            <div className="modal-body-content">
              <div className="form-content">
                <div className="form-fields-column">
                  <div className="form-field">
                    <label className="form-field-label">Upload CSV</label>
                    <div className="input-with-suffix">
                      <input
                        type="text"
                        className="form-input-field"
                        readOnly
                        placeholder="Choose .xlsx or .csv file"
                        value={importFileName}
                        onClick={() => document.getElementById('import-file-input').click()}
                      />
                      <span className="input-suffix" style={{cursor:'pointer'}} onClick={() => document.getElementById('import-file-input').click()}>📁</span>
                    </div>
                    <p className="document-hint">Use the Export as template. Only .csv is supported.</p>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Validation Summary (by Mobile Number)</label>
                    <div className="validation-summary" style={{display:'flex', gap:'12px', flexWrap:'wrap'}}>
                      <span className="status-badge active">{importValidDrivers.length} Valid</span>
                      <span className="status-badge inactive">{importInvalidRows.length} Invalid</span>
                    </div>
                    {importInvalidRows.length > 0 && (
                      <div style={{marginTop:'10px', maxHeight:'160px', overflow:'auto', border:'1px solid #e5e7eb', borderRadius:'6px', padding:'8px'}}>
                        {importInvalidRows.slice(0, 50).map((r, i) => (
                          <div key={i} style={{fontSize:'0.875rem', color:'#dc2626', padding:'4px 0', borderBottom: i < importInvalidRows.length - 1 ? '1px solid #f3f4f6' : 'none'}}>
                            Row {r.row}: {r.reason} {r.driverName && `(${r.driverName})`}
                          </div>
                        ))}
                        {importInvalidRows.length > 50 && (
                          <div style={{fontSize:'0.875rem', color:'#6b7280', padding:'4px 0', marginTop:'4px'}}>
                            ... and {importInvalidRows.length - 50} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-bar">
              <button className="modal-btn-cancel" onClick={handleImportCancel}>Cancel</button>
              <button 
                className="modal-btn-save" 
                onClick={handleImportConfirm}
                disabled={importValidDrivers.length === 0}
                style={{opacity: importValidDrivers.length === 0 ? 0.5 : 1, cursor: importValidDrivers.length === 0 ? 'not-allowed' : 'pointer'}}
              >
                Import {importValidDrivers.length > 0 && `(${importValidDrivers.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Success Modal */}
      {showImportSuccess && (
        <div className="success-modal-overlay" onClick={() => setShowImportSuccess(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Import Successful!</h2>
            <p className="success-message">
              {importSuccessCount} driver{importSuccessCount !== 1 ? 's' : ''} imported successfully.
              {importSkippedCount > 0 && ` ${importSkippedCount} row${importSkippedCount !== 1 ? 's' : ''} skipped.`}
            </p>
            <button className="success-ok-btn" onClick={() => setShowImportSuccess(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyDriverManagement;
